/* ============================================================
   Fit-for-Work Readiness — PDF report (client-side, jsPDF + autotable).

   Built entirely from the SANITIZED report the server returns, so the PDF —
   like the screen — shows Blood Pressure as a range band only, never a mmHg
   number, and never exposes the banding thresholds.
   ============================================================ */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { PARAM_META } from "../config";

const RAG = {
  green: [26, 143, 76],
  amber: [201, 138, 15],
  red: [192, 57, 43],
  na: [138, 148, 166],
};
const BLUE = [15, 117, 201];
const INK = [20, 32, 46];
const MUTED = [91, 107, 125];
const LINE = [223, 230, 238];

const asset = (p) => `${process.env.PUBLIC_URL || ""}${p}`;

/* jsPDF's built-in Helvetica is WinAnsi-only — glyphs like ≥ ≤ ₂ render as
   stray quotes. Replace them with ASCII equivalents for the PDF surface. */
const A = (s) =>
  String(s == null ? "" : s)
    .replace(/≥/g, ">=")
    .replace(/≤/g, "<=")
    .replace(/₂/g, "2")
    .replace(/−/g, "-");

async function loadImage(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

// Build the detailed-vitals rows from the sanitized server params.
function vitalRows(report, tr) {
  return (report.params || []).map((p) => {
    const meta = PARAM_META[p.key] || {};
    const label = meta.labelKey ? tr(meta.labelKey) : p.key;
    let result;
    if (meta.band) {
      const k = meta.bandLabelKey && meta.bandLabelKey[p.band];
      result = k ? tr(k) : String(p.band || "—");
      // Server-provided band range (e.g. BP): "Normal (115–125 / 75–80)".
      if (p.bandRange) result = `${result} (${p.bandRange})`;
    } else {
      result = `${p.value}${meta.unit ? " " + meta.unit : ""}`;
    }
    return { label: A(label), result: A(result), range: A(meta.range || "—"), status: p.status || "na" };
  });
}

const statusWord = (tr, s) =>
  s === "green" ? tr("result.statusNormal")
  : s === "amber" ? tr("result.statusWatch")
  : s === "red" ? tr("result.statusAction")
  : "—";

export async function generateReportPdf({ report, verdict: combined, pulmo, pvt, alertness, employee, tr }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  // The combined verdict (face + pulmo + alertness, weighted) is passed in by
  // the Result screen; fall back to the face-scan verdict if absent.
  const verdict = combined || report.verdict || "clear";
  const vStatus = verdict === "clear" ? "green" : verdict === "caution" ? "amber" : "red";

  const [lt, cp] = await Promise.all([
    loadImage(asset("/brand/lt-logo.png")),
    loadImage(asset("/brand/careplix-logo.png")),
  ]);

  /* ── Header band ── */
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, W, 74, "F");
  // The L&T logo is dark-blue-on-transparent, so it disappears on the blue
  // band — sit it on a white chip so it stays legible.
  if (lt) {
    try {
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(M - 8, 14, 106, 46, 6, 6, "F");
      const props = doc.getImageProperties(lt);
      const h = 30, w = Math.min(90, (props.width / props.height) * h);
      doc.addImage(lt, "PNG", M, 22, w, h);
    } catch {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(tr("result.reportHeading"), W - M, 34, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(new Date().toLocaleString(), W - M, 52, { align: "right" });

  let y = 96;

  /* ── Worker line ── */
  const emp = employee || {};
  const bits = [];
  if (emp.name) bits.push(emp.name);
  if (emp.age) bits.push(`${tr("identify.fAge")}: ${emp.age}`);
  if (emp.gender) bits.push(`${tr("identify.fGender")}: ${emp.gender}`);
  doc.setTextColor(...MUTED);
  doc.setFontSize(10);
  doc.text(bits.length ? bits.join("   ·   ") : tr("result.worker"), M, y);
  y += 18;

  /* ── Verdict box ── */
  const vc = RAG[vStatus];
  doc.setDrawColor(...vc);
  doc.setFillColor(vc[0], vc[1], vc[2]);
  doc.setGState && doc.setGState(new doc.GState({ opacity: 0.08 }));
  doc.roundedRect(M, y, W - M * 2, 58, 6, 6, "F");
  doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));
  doc.setDrawColor(...vc);
  doc.setLineWidth(1);
  doc.roundedRect(M, y, W - M * 2, 58, 6, 6, "S");
  doc.setFillColor(...vc);
  doc.circle(M + 28, y + 29, 13, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(vStatus === "green" ? "✓" : vStatus === "amber" ? "!" : "✕", M + 28, y + 34, { align: "center" });
  doc.setTextColor(...INK);
  doc.setFontSize(13);
  doc.text(tr(`result.${verdict}Title`), M + 52, y + 25);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  const bodyLines = doc.splitTextToSize(tr(`result.${verdict}Body`), W - M * 2 - 64);
  doc.text(bodyLines.slice(0, 2), M + 52, y + 40);
  y += 74;

  /* ── Headline scores ── */
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(tr("result.scoresTitle"), M, y);
  y += 10;
  const scores = [
    { label: tr("result.wellbeing"), value: report.wellbeing },
    { label: tr("result.pulmonary"), value: pulmo && pulmo.score != null ? pulmo.score : null },
    { label: tr("result.alertness"), value: alertness },
  ];
  const cardW = (W - M * 2 - 20) / 3;
  scores.forEach((s, i) => {
    const x = M + i * (cardW + 10);
    const st = s.value == null ? "na" : s.value >= 60 ? "green" : s.value >= 40 ? "amber" : "red";
    doc.setDrawColor(...LINE);
    doc.setLineWidth(1);
    doc.roundedRect(x, y, cardW, 54, 6, 6, "S");
    doc.setTextColor(...RAG[st]);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(s.value == null ? tr("result.na") : String(s.value), x + 12, y + 30);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(s.label, x + 12, y + 46);
  });
  y += 74;

  /* ── Detailed vitals table ── */
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[tr("result.colParam"), tr("result.colResult"), tr("result.colRange"), tr("result.colStatus")]],
    body: vitalRows(report, tr).map((r) => [r.label, r.result, r.range, statusWord(tr, r.status)]),
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 6, textColor: INK, lineColor: LINE, lineWidth: 0.5 },
    headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [246, 249, 252] },
    columnStyles: { 3: { halign: "center", fontStyle: "bold" } },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 3) {
        const rows = vitalRows(report, tr);
        const st = rows[data.row.index] && rows[data.row.index].status;
        if (RAG[st]) data.cell.styles.textColor = RAG[st];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;

  /* Free-text note helper: page-breaks if the note would collide with the
     fixed footer band (footer occupies the last ~60pt of the page). */
  const drawNote = (text) => {
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(A(text), W - M * 2);
    const h = 8 + lines.length * 10;
    const pageH = doc.internal.pageSize.getHeight();
    if (y + h > pageH - 70) { doc.addPage(); y = M; }
    doc.text(lines, M, y + 8);
    y += h;
  };

  /* Diabetes-risk definition note (only when the row is present) — wording
     follows the worker's actual HbA1c band. */
  if ((report.params || []).some((p) => p.key === "diabRisk")) {
    const hba1c = (report.params || []).find((p) => p.key === "hba1c");
    const key = hba1c && hba1c.band === "aboveOptimal" ? "result.diabRiskNoteAbove" : "result.diabRiskNoteOptimal";
    drawNote(tr(key));
  }
  y += 8;

  /* ── Lung screening ── */
  if (pulmo) {
    const oadTxt = pulmo.oad == null ? tr("result.unavailable") : pulmo.oad ? tr("result.oadTrue") : tr("result.oadFalse");
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[tr("result.pulmonary"), ""]],
      body: [
        [tr("result.pulmoScore"), pulmo.score != null ? String(pulmo.score) : tr("result.unavailable")],
        [tr("result.pFevFvc"), pulmo.fevFvc != null ? String(pulmo.fevFvc) : tr("result.unavailable")],
        [tr("result.pOad"), oadTxt],
      ],
      styles: { font: "helvetica", fontSize: 9.5, cellPadding: 6, textColor: INK, lineColor: LINE, lineWidth: 0.5 },
      headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontStyle: "bold" },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  /* ── Alertness ── */
  if (pvt) {
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[tr("result.attention"), ""]],
      body: [
        [tr("result.alertness"), alertness != null ? String(alertness) : tr("result.na")],
        [tr("result.attentionMean"), `${pvt.mean} ${tr("result.uMs")}`],
        [tr("result.attentionLapses"), String(pvt.lapses)],
      ],
      styles: { font: "helvetica", fontSize: 9.5, cellPadding: 6, textColor: INK, lineColor: LINE, lineWidth: 0.5 },
      headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontStyle: "bold" },
    });
    y = doc.lastAutoTable.finalY + 10;
    // What average reaction + lapses indicate.
    drawNote(tr("result.attentionNote"));
    y += 8;
  }

  /* ── Disclaimer footer ── */
  const ph = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...LINE);
  doc.line(M, ph - 54, W - M, ph - 54);
  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const disc = doc.splitTextToSize(tr("result.disclaimer"), W - M * 2 - 90);
  doc.text(disc, M, ph - 40);
  if (cp) {
    try {
      const props = doc.getImageProperties(cp);
      const h = 16, w = Math.min(96, (props.width / props.height) * h);
      doc.addImage(cp, "PNG", W - M - w, ph - 46, w, h);
    } catch {}
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`fit-for-work-${(emp.name || "worker").replace(/\s+/g, "-").toLowerCase()}-${stamp}.pdf`);
}
